import { categorizeSkill, GROWTHOS_SKILLS } from "./skillTaxonomy";
import { hermesSkillDetails, hermesReferenceText } from "./hermesSkillDetails";

// Customer copy for the managed skill catalog, reviewed against kit/skills.
// These are display aliases only: registry IDs/names and history keys stay intact.
const GROWTHOS_CATALOG = {
  "client-ad-generation": [
    "Static Ad Generation",
    "Create a batch of static ads from your products, offers, and brand direction, with creative review before launch.",
  ],
  "static-graphic-production": ["Static Ad Design"],
  "connector-onboarding": [
    "App Connections",
    "Get guided help connecting the tools your agent needs for your brand’s work.",
  ],
  "creative-brain": [
    "Creative Strategy",
    "Develop ad angles, hooks, and creative plans using your brand’s context and GrowthOS creative guidance.",
  ],
  "ecommerce-ops": [
    "Ecommerce Operations",
    "Review and improve your store and email marketing workflows.",
  ],
  "first-brand-deep-scan": [
    "Brand Growth Audit",
    "Review your brand’s website, offers, marketing assets, and available performance data to identify growth opportunities.",
  ],
  "growth-asset-lego-backfill": [
    "Creative Library Organization",
    "Organize existing ads, pages, emails, and product assets into a reusable growth library.",
  ],
  "work-project-management": [
    "Project Management",
    "Turn requests into clear tasks, priorities, owners, and progress updates.",
  ],
  "hermes-desktop-access": [
    "Agent Desktop",
    "Get help accessing your agent’s desktop workspace.",
  ],
  "lifecycle-email-system": [
    "Email & SMS Marketing",
    "Audit and plan lifecycle flows, campaigns, segmentation, and email or SMS calendars.",
  ],
  "mer-report": [
    "Daily Growth Report",
    "Review revenue, ad spend, performance trends, and prioritized next actions in a daily growth report.",
  ],
  "paid-traffic-diagnostic": [
    "Ad Performance Analysis",
    "Find what is driving ad performance, creative fatigue, acquisition costs, and opportunities to improve.",
  ],
  "pdp-cro-aov-optimizer": [
    "Product Page Optimization",
    "Review product pages, offers, and buying experience to improve conversion and average order value.",
  ],
  "retention-ltv-planner": [
    "Customer Retention Planning",
    "Plan repeat-purchase, subscription, loyalty, and post-purchase improvements.",
  ],
  "setup-messages": [
    "Team Updates",
    "Set up or adjust recurring project updates, reminders, and team summaries.",
  ],
  "shopify-growth-operations": [
    "Shopify Growth",
    "Analyze store performance and plan improvements to merchandising, conversion, and customer retention.",
  ],
  "slack-client-channel": [
    "Team Communication",
    "Get help setting up and using your brand’s supported team communication channel.",
  ],
  "smart-business-systems": [
    "Growth Planning & Execution",
    "Turn growth strategy into practical plans, responsibilities, and team routines.",
  ],
  "unit-economics-benchmarking": [
    "Profitability & Growth Benchmarks",
    "Understand acquisition costs, margins, customer value, and growth metrics in context.",
  ],
};

// Internal operating/support procedures, not capabilities to advertise to clients.
// Match exact managed names; never hide a skill based on incidental keywords.
const INTERNAL_SKILLS = new Set([
  "firecrawl-developer-index",
  "agent-launch-ops",
  "backend-brain-rag-guardrails",
  "blocked-checkpoint-handoff",
  "client-build",
  "client-silo-guardrails",
  "dashboard-source-of-truth",
  "launch-retrospective",
  "new-client-launch",
  "secure-dm-credential-ingress",
  "supervised-provider-access",
]);

const rawName = (skill) => String(skill?.name || skill?.skillName || "");
const normalizedName = (name) =>
  String(name)
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, "-");
const catalogKey = (name) =>
  normalizedName(name)
    .replace(/^(?:(?:xscale|growthos)-)+/, "")
    // The client API calls WorkGraph "work"; both spellings identify one skill.
    .replace(/^workgraph-/, "work-");
const isManaged = (skill) =>
  categorizeSkill(skill) === GROWTHOS_SKILLS ||
  /^(?:xscale|growthos)[\s_-]/i.test(rawName(skill));
const catalogEntry = (skill) => {
  const key = catalogKey(rawName(skill));
  return isManaged(skill) && Object.hasOwn(GROWTHOS_CATALOG, key)
    ? GROWTHOS_CATALOG[key]
    : undefined;
};

const ACRONYMS = {
  ai: "AI",
  api: "API",
  apis: "APIs",
  aov: "AOV",
  cro: "CRO",
  dm: "DM",
  kpi: "KPI",
  ltv: "LTV",
  mer: "MER",
  pdp: "Product Page",
  rag: "Knowledge",
  seo: "SEO",
  sms: "SMS",
  ugc: "UGC",
  oauth: "Account Access",
};
const titleWords = (name) =>
  String(name)
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((word) =>
      Object.hasOwn(ACRONYMS, word.toLowerCase())
        ? ACRONYMS[word.toLowerCase()]
        : word[0].toUpperCase() + word.slice(1).toLowerCase(),
    )
    .join(" ");

export function isClientVisibleSkill(skill) {
  return !isManaged(skill) || !INTERNAL_SKILLS.has(catalogKey(rawName(skill)));
}

export function customerSkillTitle(skill) {
  const entry = catalogEntry(skill);
  if (entry) return `GrowthOS ${entry[0]}`;
  if (isManaged(skill))
    return `GrowthOS ${titleWords(catalogKey(rawName(skill))) || "Skill"}`.replace(
      /xscale/gi,
      "GrowthOS",
    );
  return customerSkillText(rawName(skill) || "Unnamed skill")
    .replace(/[-_]+/g, " ")
    .replace(/^\w/, (letter) => letter.toUpperCase());
}

export function customerSkillText(value) {
  return String(value ?? "")
    .replace(/\b(?:xscale|growthos)(?:[-_][a-z0-9]+)+\b/gi, (name) =>
      customerSkillTitle({ name }),
    )
    .replace(/xscale/gi, "GrowthOS");
}

export function customerSkillDescription(skill) {
  // Brand adaptations keep their own description instead of shared catalog copy.
  return (
    (categorizeSkill(skill) === GROWTHOS_SKILLS && catalogEntry(skill)?.[1]) ||
    customerSkillText(skill?.description).trim() ||
    hermesReferenceText(hermesSkillDetails(skill)?.description)
  );
}

export function customerSkillSearchText(skill) {
  const reference = hermesSkillDetails(skill);
  return [
    rawName(skill),
    customerSkillTitle(skill),
    customerSkillDescription(skill),
    reference?.category,
    ...(reference?.tags || []),
  ]
    .filter(Boolean)
    .join(" ");
}
